import type { Item } from '@/types/prioritization/lifecycle';
import {
  ITEM_PRINT_FIELD_IDS,
  ITEM_PRINT_FIELD_LABELS,
  type ItemPrintFieldId,
  type ItemPrintFieldVisibility,
  formatItemFieldValue,
} from '@/lib/print/itemPrintPreferences';

export interface ItemPrintBodyProps {
  item: Item;
  visibility: ItemPrintFieldVisibility;
  queueLabel?: string | null;
}

function DocHeading({ item, visibilityTitle }: { item: Item; visibilityTitle: boolean }) {
  const titleText = item.title?.trim() || '';
  if (visibilityTitle && titleText) {
    return <h1 className="text-2xl font-semibold tracking-tight text-[var(--text-pri)] print:text-black">{titleText}</h1>;
  }
  if (visibilityTitle) {
    return <h1 className="text-xl font-semibold text-[var(--text-sec)] print:text-[var(--text-sec)]">Untitled item</h1>;
  }
  return (
    <h1 className="text-lg font-medium text-[var(--text-sec)] print:text-[var(--text-sec)]">
      <span className="font-mono text-sm text-[var(--text-pri)] print:text-[var(--text-pri)]">{item.id}</span>
    </h1>
  );
}

export function ItemPrintBody({ item, visibility, queueLabel }: ItemPrintBodyProps) {
  const fmtOpts = { queueLabel };
  const fieldRows: ItemPrintFieldId[] = ITEM_PRINT_FIELD_IDS.filter((id) => visibility[id] && id !== 'title');

  return (
    <div className="space-y-8 text-[13px] leading-relaxed">
      <div className="print-field-block border-b border-[var(--rule)] pb-5 space-y-1">
        <DocHeading item={item} visibilityTitle={visibility.title} />
        <p className="text-[11px] uppercase tracking-wider text-[var(--text-ter)] print:text-[var(--text-ter)]">
          Workspace item · Priorities.ai print view · {new Date().toLocaleString()}
        </p>
      </div>

      <div className="space-y-6">
        {fieldRows.map((field) => {
          const label = ITEM_PRINT_FIELD_LABELS[field];
          const value = formatItemFieldValue(field, item, fmtOpts);
          const body = value.trim().length ? value : '—';
          return (
            <section key={field} className="print-field-block space-y-1.5">
              <h2 className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] print:text-[var(--text-sec)] border-b border-[var(--rule)] pb-1">
                {label}
              </h2>
              <div className="whitespace-pre-wrap text-[var(--text-pri)] print:text-black">{body}</div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
